import { useState, useEffect } from 'react';
import { MessageCircle, Layers, ClipboardCheck, BookText, FileSignature, Volume2 } from 'lucide-react';
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import YTVideo from "./YTVideo"; 
import ChatSection from '../chat/ChatSection';
import { useSidebar } from '../../context/SidebarContext';
import VideoPlayer from "./VideoPlayer";
import TextContent from "./TextContent";
import FlashcardsSection from '../learning/FlashcardsSection';
import QuizSection from '../learning/QuizSection';
import NotesSection from '../learning/NotesSection';
import SummarySection from '../learning/SummarySection';
import { useParams, useSearchParams, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import PDFViewer from './PDFViewer';
import AudioExplainer from '../learning/AudioExplainer';

import useContent from '../../hooks/useContent';
import useChat from '../../hooks/useChat';
import { buildContextForAI } from '../../utils/contentUtils';
import { generateSamplePromptsForSession } from '../../utils/firebaseService';
import { fetchPermittedModels } from '../../utils/fetchSubscription';

import CourseHeader from './CourseHeader';
import ContentTypeSelector from './ContentTypeSelector';
import Skeleton from './Skeleton';
import MobileTabs from './MobileTabs';
import DesktopTabs from './DesktopTabs';

export default function CoursePanel() {
	const { contentId: routeContentId } = useParams();
	const [searchParams] = useSearchParams();
	const location = useLocation();
	const { user } = useAuth();
	let contentId = routeContentId || searchParams.get('contentId');
	if (!contentId && location.pathname.startsWith('/c/')) {
		contentId = location.pathname.split('/c/')[1].split('/')[0];
	}
	const { collapsed, isMobile } = useSidebar();
	const [activeRightTab, setActiveRightTab] = useState('chat');
	const { currentContent, contentType, setContentType, isLoading } = useContent(contentId, user);

	const {
		chatMessages,
		chatInput,
		setChatInput,
		handleChatSubmit,
		isAILoading
	} = useChat({ contentId, user, currentContent, contentType });

	const [youtubeContents] = useState([]);

	const [samplePrompts, setSamplePrompts] = useState([]);
	const [permittedModels, setPermittedModels] = useState([]);

	const [chatBootstrap, setChatBootstrap] = useState(true);

	useEffect(() => {
		let active = true;
		(async () => {
			if (!user?.uid) { setPermittedModels([]); return; }
			try {
				const models = await fetchPermittedModels(user.uid);
				if (active) {
					setPermittedModels(models || []);
				}
			} catch (e) {
				if (active) {
					setPermittedModels([]);
				}
			}
		})();
		return () => { active = false; };
	}, [user?.uid]);

	useEffect(() => {
		setSamplePrompts([]);
		setChatBootstrap(true);
	}, [contentId]);

	useEffect(() => {
		if (!contentId || !currentContent) return;
		let active = true;
		generateSamplePromptsForSession(contentId)
			.then((prompts) => { if (active) setSamplePrompts(prompts || []); })
			.catch(() => {});
		return () => { active = false; };
	}, [contentId, currentContent]);

	useEffect(() => {
		if (chatMessages && chatMessages.length > 0) setChatBootstrap(false);
	}, [chatMessages]);

	const renderContent = () => {
		if (isLoading) return <Skeleton />;
		// Redirect to home if contentId is missing or content not found
		if (!contentId || !currentContent) {
			return <Navigate to="/" replace state={{ from: location.pathname }} />;
		}

		const type = (contentType || 'text').toLowerCase();
		
		const getContainerStyle = () => {
			if (isMobile) {
				return { 
					height: '60vh', 
					overflowY: 'auto', 
					border: '1px solid #eaeaea', 
					borderRadius: '8px',
					WebkitOverflowScrolling: 'touch' 
				};
			} else {
				return { 
					height: '700px', 
					overflowY: 'auto', 
					border: '1px solid #eaeaea', 
					borderRadius: '8px' 
				};
			}
		};

		switch (type) {
			case 'youtube': {
				const ytId = currentContent.videoID || (currentContent.metadata && currentContent.metadata.videoId) || currentContent.source_id || currentContent.youtube_id;
				return <YTVideo videoId={ytId} />;
			}
			case 'pdf': 
			case 'txt':
			case 'doc':
			case 'docx': {
				const storageUrl = currentContent.storage_url;
				const fileUrl = currentContent.file_path || currentContent.source_url || currentContent.url;
				return (
					<PDFViewer
						storageUrl={storageUrl}
						pdfUrl={fileUrl}
						contentId={contentId}
						onInsertToChat={(text) => {
							try {
								setChatInput(prev => {
									const prefix = prev && prev.trim() ? prev + " " : "";
									return prefix + text;
								});
								setActiveRightTab('chat');
								setTimeout(() => {
									const ta = document.querySelector('textarea[placeholder="Ask any question"]');
									if (ta) {
										try { ta.focus(); ta.setSelectionRange(ta.value.length, ta.value.length); } catch(e){/* ignore */ }
									}
								}, 80);
							} catch (e) {
							}
						}}
					/>
				);
			}
			case 'web':
				return (
					<div className="content-container" style={getContainerStyle()}>
						<TextContent content={currentContent.content || ''} />
					</div>
				);
			case 'video':
				return <VideoPlayer videoUrl={currentContent.source_url || currentContent.video_url} />;
			case 'text':
			default: {
				const textContent = currentContent.content || currentContent.text_content || 'No content available';
				return (
					<div className="content-container" style={getContainerStyle()}>
						<TextContent content={textContent} />
					</div>
				);
			}
		}
	};

	const renderRightTabContent = () => {
		const title = currentContent?.title || "";
		const extractedContent = buildContextForAI(currentContent, contentType);

		switch (activeRightTab) {
			case 'chat':
				const adhoc = !!(currentContent && !currentContent.source_type);
				const showBootstrapOverlay = adhoc && (!chatMessages || chatMessages.length === 0) && chatBootstrap;

				return (
					<div className="relative h-full">
						{showBootstrapOverlay && (
							<div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 dark:bg-gray-900/70">
								<span className="inline-block h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
								<span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Setting up your chat…</span>
							</div>
						)}
						<ChatSection 
							chatMessages={chatMessages} 
							chatInput={chatInput} 
							setChatInput={setChatInput} 
							handleChatSubmit={handleChatSubmit}
							isLoading={isAILoading || showBootstrapOverlay}
							samplePrompts={samplePrompts}
							uid={user?.uid}
							permittedModels={permittedModels}
						/>
					</div>
				);
			case 'flashcards':
				return <FlashcardsSection contentId={contentId} title={title} content={extractedContent} contentType={contentType} />;
			case 'quizzes':
				return <QuizSection contentId={contentId} title={title} content={extractedContent} contentType={contentType} />;
			case 'notes':
				return <NotesSection contentId={contentId} content={extractedContent} contentType={contentType} />;
			case 'summary':
				return <SummarySection contentId={contentId} transcriptOrContent={extractedContent} contentType={contentType} videoDuration={currentContent?.duration} />;
			case 'audio':
				return <AudioExplainer contentId={contentId} title={title} />;
			default:
				return null;
		}
	};

	const tabs = (() => {
		let base = [
			{ id: 'chat', label: 'Chat', icon: <MessageCircle size={18} />},
			{ id: 'summary', label: 'Summary', icon: <BookText size={18} /> },
			{ id: 'flashcards', label: 'Flashcards', icon: <Layers size={18} />},
			{ id: 'quizzes', label: 'Quizzes', icon: <ClipboardCheck size={18} /> },
			{ id: 'notes', label: 'Notes', icon: <FileSignature size={18} /> },
			{ id: 'audio', label: 'Audio', icon: <Volume2 size={18} /> }
		];
		if (currentContent && !currentContent.source_type) {
			base = base.filter(t => t.id === 'chat');
		}
		return base;
	})();

	const isAdhoc = !!(currentContent && !currentContent.source_type);

	return (
		<div 
			id="course-panel-content"
			className="h-screen transition-all duration-300 ease-in-out mt-20 p-3 bg-white dark:bg-gray-900 dark:text-white"
			style={{ marginLeft: !isMobile && !collapsed ? '16rem' : '0', zIndex: 10, backgroundImage: 'radial-gradient(circle at 30px 30px, rgba(200,200,200,0.05) 0%, rgba(200,200,200,0.05) 2px, transparent 2px)' }}
		>
			{isAdhoc ? (
				<div className="h-full flex flex-col">
					<CourseHeader title={currentContent?.title || 'New Chat'} />
					<div className="flex-1 mt-4 overflow-hidden">
						{/* Direct chat only (no tabs, no content panel) */}
						{renderRightTabContent()}
					</div>
				</div>
			) : (
				isMobile ? (
					<div className="flex flex-col h-full overflow-y-auto relative">
						<div className="sticky top-0 bg-white dark:bg-gray-900 pb-2 backdrop-blur-sm bg-opacity-90 dark:bg-opacity-90">
							<CourseHeader title={currentContent?.title || "Course Content"} />
							<div className="px-2">
								<ContentTypeSelector
									youtubeContents={youtubeContents}
									currentContent={currentContent}
									onSelect={(c) => { /* setCurrentContent if you support list */ setContentType('youtube'); }}
								/>
							</div>
						</div>
						<div className="w-full mb-4 relative z-10 rounded-lg overflow-hidden shadow-md">
							{renderContent()}
						</div>
						<div className="p-2 mb-4 top-12 bg-white dark:bg-gray-900 z-40 backdrop-blur-sm bg-opacity-90 dark:bg-opacity-90 border-b border-t border-gray-200 dark:border-gray-700 overflow-visible relative">
							<MobileTabs
								tabs={tabs}
								activeId={activeRightTab}
								onTabClick={setActiveRightTab}
							/>
						</div>
						<div id="mobile-tab-content" className="flex-1 relative bg-white dark:bg-gray-800 rounded-lg shadow-md">
							{renderRightTabContent()}
						</div>
					</div>
				) : (
					<PanelGroup direction="horizontal">
						<Panel defaultSize={50} minSize={40}>
							<div className="h-full overflow-y-auto mt-6">
								<CourseHeader title={currentContent?.title || "Course Content"} />
								<div className="px-4 py-2">
									<ContentTypeSelector
										youtubeContents={youtubeContents}
										currentContent={currentContent}
										onSelect={(c) => { /* setCurrentContent if you support list */ setContentType('youtube'); }}
									/>
								</div>
								<div className="p-4">{renderContent()}</div>
							</div>
						</Panel>
						<PanelResizeHandle className="w-2 bg-gray-200 cursor-col-resize border-h" />
						<Panel defaultSize={50} minSize={35}>
							<div className="h-full flex flex-col border-l mt-2">
								<div className="p-2 border-b border-gray-200 dark:border-gray-700">
									<DesktopTabs
										tabs={tabs}
										activeId={activeRightTab}
										onTabClick={setActiveRightTab}
									/>
								</div>
								<div className="flex-1 overflow-hidden">
									{renderRightTabContent()}
								</div>
							</div>
						</Panel>
					</PanelGroup>
				)
			)}
		</div>
	);
}